/**
 * Silencia los logs ruidosos de MediaPipe / TensorFlow Lite en consola.
 * - Filtra mensajes con patrones conocidos de vision_wasm_internal.js
 * - Bloquea telemetría a odml.pa.googleapis.com (403 ignorado)
 * - F8.68: captura unhandledrejection de WASM init concurrente (M_ID)
 * Importar UNA VEZ al inicio de la app (main.jsx o App.jsx).
 */

const MEDIAPIPE_PATTERNS = [
  "vision_wasm_internal.js",
  "face_landmarker_graph",
  "gl_context",
  "Sets FaceBlendshapesGraph",
  "OpenGL error checking",
  "Graph successfully started",
  "Graph finished closing",
  "Created TensorFlow Lite XNNPACK",
  "Successfully destroyed WebGL context",
  "INFO: Created TensorFlow Lite",
  "WARNING: All log messages",
  /^[IWE]\d{4} \d{2}:\d{2}:\d{2}\.\d+/,  // glog timestamps
  // ── TensorFlow.js / Human.js (ruido de init) ──
  "already been set. Overwriting the platform",
  "backend was already registered. Reusing existing backend",
  "is already registered",
  "The powerPreference option is currently ignored",
  "Human: version:",
  "webgpu adapter info",
  "initializeBackend",
  "initializeBackendsAndReturnBest",
]

function shouldSilence(args) {
  if (!args || args.length === 0) return false
  const first = args[0]
  if (typeof first !== "string") return false
  return MEDIAPIPE_PATTERNS.some((p) =>
    typeof p === "string" ? first.includes(p) : p.test(first)
  )
}

/**
 * F8.68: reconoce errores de WASM init concurrente (TF.js + MediaPipe).
 * Firma: TypeError "Cannot read properties of undefined (reading 'M_ID')"
 * o cualquier error de `createWasm` / `instantiateAsync` / `initializeBackend`.
 * Es ruido — Human.js cae al siguiente backend (webgl/cpu) automáticamente.
 */
function isWasmInitNoise(reason) {
  if (!reason) return false
  const msg = String(reason.message || reason.error?.message || reason)
  if (msg.includes("M_ID")) return true
  const stack = String(reason.stack || reason.error?.stack || "")
  if (
    stack.includes("tfjs-backend-wasm") ||
    stack.includes("vision_wasm_internal") ||
    stack.includes("createWasm") ||
    stack.includes("instantiateAsync")
  ) {
    return true
  }
  return false
}

let installed = false
export function installMediaPipeSilencer() {
  if (installed) return
  installed = true

  // ── 1. Filtro de console ──
  const ORIG = {
    log: console.log.bind(console),
    info: console.info.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console),
    debug: console.debug.bind(console),
  }

  const wrap = (level) => (...args) => {
    if (shouldSilence(args)) return
    ORIG[level](...args)
  }

  console.log = wrap("log")
  console.info = wrap("info")
  console.warn = wrap("warn")
  console.error = wrap("error")
  console.debug = wrap("debug")

  // ── 2. F8.68: capturar unhandledrejection de WASM init ──
  // Estos son errores que no se pueden atrapar con console.error patch
  // (aparecen como "Uncaught (in promise)" directo del browser).
  window.addEventListener("unhandledrejection", (event) => {
    if (isWasmInitNoise(event.reason)) {
      event.preventDefault()
    }
  })

  // ── 3. F8.68: capturar error event de WASM init (para iOS Safari) ──
  window.addEventListener("error", (event) => {
    if (isWasmInitNoise(event.error || event)) {
      event.preventDefault()
    }
  })

  // ── 4. Bloqueo de telemetría a odml.pa.googleapis.com ──
  // ⚠ NO patcheamos window.fetch. Amplify también lo patchea y rompe.
  // La telemetría a odml.pa es inofensiva: recibe 403 silencioso.

  ORIG.info("[silenceMediaPipe] instalado (F8.68: +wasm noise)")
}
